import { useState, useMemo, useEffect, useCallback } from "react";
import { Button, InputField, SelectMode } from "@/components";
import { ServiceabilityTable, SpaceWrapper } from "../../components";
import { useApi, useValidation, useFieldError } from "@/";
import RateCalcSchema from "../../schemas/RateCalcSchema";
import { useSelector } from "react-redux";
import { fa } from "@faker-js/faker";
const modeOptions = [
  { label: "Air Mode", value: "Air" },
  { label: "Surface Mode", value: "Surface" },
];
const paymentOption = [
  { value: "Prepaid", label: "Prepaid" },
  { value: "COD", label: "Cash on Delivery" },
];

const RateCalculator = () => {
  const user = useSelector((state) => state.user?.user) || {};
  const courierSettings = useSelector(
    (state) => state.setting?.settings?.courierManagement,
  );
  const serviceableCouriers = useSelector(
    (state) => state.app?.appData?.courier,
  );
  console.log("User:::", user);
  const [formData, setFormData] = useState({
    pickupPincode: "",
    deliveryPincode: "",
    weight: "",
    length: "",
    breadth: "",
    height: "",
    paymentMode: "Prepaid",
    serviceMode: "Surface",
    shipmentValue: "",
  });

  const [data, setData] = useState([]);
  const [codCol, setCodCol] = useState("Prepaid");
  const [generalError, setGeneralError] = useState("");
  const { callApi, error, loading } = useApi();
  const { validate, errors } = useValidation(RateCalcSchema);
  const [formErrors, setFormErrors] = useState(errors);
  const getErrorMessage = useFieldError(formErrors);

  useEffect(() => {
    if (error) setGeneralError(error);
  }, [error]);

  useEffect(() => {
    setFormErrors(errors);
  }, [errors]);

  useEffect(() => {
    if (!loading) console.log("Data updated", data);
  }, [loading]);

  const handleChange = useCallback((key, value) => {
    let updatedValue = value;

    if (key === "weight") {
      const num = parseFloat(value);

      if (isNaN(num)) {
        updatedValue = "";
      } else {
        // prevent negative numbers
        updatedValue = num < 0 ? 0 : num;
      }
    }

    setFormData((prev) => ({ ...prev, [key]: updatedValue }));
  }, []);

  const handleSubmit = async (e) => {
    setFormErrors([]);
    setGeneralError("");
    console.log("clicked");
    e.preventDefault();
    try {
      // Normalize weight (minimum 0.5kg)
      const normalizedFormData = {
        ...formData,
        weight: Number(formData.weight) < 0.5 ? 0.5 : Number(formData.weight),
      };
      console.log("Calling API with:", normalizedFormData);
      const result = validate(normalizedFormData);
      if (!result.isValid) {
        setFormErrors(result.errors);
        return;
      }

      const response = await callApi("common/rate-calculator", "GET", null, {
        ...normalizedFormData,
        userId: user?._id,
      });

      console.log("Response from API:", response);
      if (response?.success && Array.isArray(response?.data?.rates)) {
        setCodCol(response?.paymentMode);

        // Get enabled courier IDs from settings
        const enabledCourierIds = new Set(
          courierSettings
            ?.filter((setting) => setting.isEnabled)
            ?.map((setting) => setting.courierId) || [],
        );

        // Create a map of courier names to IDs
        const courierNameToId = {};
        serviceableCouriers?.forEach((courier) => {
          courierNameToId[courier.name] = courier._id;
        });

        const formattedData = response?.data?.rates
          ?.map((rate) => ({
            courierName: rate?.courierName,
            courierId: courierNameToId[rate?.courierName],
            shippingCharge: rate?.zonePrice,
            codCharges:
              formData.paymentMode === "COD"
                ? Number(rate?.codCharges?.toFixed(2))
                : null,
            gstCharges: Number(
              (
                (rate?.zonePrice +
                  (formData.paymentMode === "COD" ? rate?.codCharges : 0)) *
                rate?.gst
              ).toFixed(2),
            ),
            totalCharge: Number(
              (
                rate.zonePrice +
                (formData.paymentMode === "COD" ? rate?.codCharges : 0) +
                (rate?.zonePrice + rate?.codCharges) * rate?.gst
              ).toFixed(2),
            ),
          }))
          ?.filter((rate) => {
            // Only show rates for enabled couriers
            return (
              enabledCourierIds.size === 0 ||
              enabledCourierIds.has(rate.courierId)
            );
          });

        // Prevent redundant state updates
        setData((prevData) =>
          JSON.stringify(prevData) === JSON.stringify(formattedData)
            ? prevData
            : formattedData,
        );
        setCodCol(response?.data?.paymentMode);
        setGeneralError("");
      } else {
        setData([]);
        setGeneralError(response.error);
        console.error(
          "Rates data is not available in the response: ",
          generalError,
        );
      }
    } catch (err) {
      setGeneralError("An unexpected error occurred. Please try again.", err);
      console.error("Error during API call:", err);
    }
  };

  const handleReset = () => {
    setFormData({
      pickupPincode: "",
      deliveryPincode: "",
      weight: "",
      length: "",
      breadth: "",
      height: "",
      paymentMode: "Prepaid",
      serviceMode: "Surface",
      shipmentValue: "",
    });
    setData([]);
    setGeneralError("");
    setFormErrors([]);
  };

  const columns = useMemo(
    () => [
      "Courier Name",
      "Shipping Charge",
      ...(codCol === "COD" ? ["COD Charges"] : []),
      "GST Charges",
      "Total Charge",
    ],
    [codCol], // Dependency ensures columns update when codCol changes
  );

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        {/* Heading */}
        <div className="flex flex-col gap-1">
          <h1 className="text-headline4 md:text-headline2 font-nunito font-bold text-secondary/95">
            Calculate Shipping Rates
          </h1>
          <p className="text-body2 md:text-subtitle2 pl-1 font-ptsans text-secondary/70 w-full md:w-[50%]">
            Plan eCommerce shipments and estimate courier charges with ICC’s
            price calculator.
          </p>
        </div>
        {generalError && <p className="text-red-600 mt-4">{generalError}</p>}
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-2 w-full md:w-[50%]"
        >
          {/* Pincode Fields */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 w-full">
            <InputField
              label="Pickup Pincode"
              type="number"
              id="pickupPincode"
              error={getErrorMessage("pickupPincode")}
              placeholder="Enter Pickup Pincode"
              value={formData.pickupPincode}
              onChange={(val) => handleChange("pickupPincode", val)}
              className="w-full"
              disabled={loading}
            />
            <InputField
              label="Delivery Pincode"
              type="number"
              id="deliveryPincode"
              error={getErrorMessage("deliveryPincode")}
              placeholder="Enter Delivery Pincode"
              value={formData.deliveryPincode}
              onChange={(val) => handleChange("deliveryPincode", val)}
              className="w-full"
              disabled={loading}
            />
          </div>

          {/* Weight Input */}
          <InputField
            label="Weight (kg)"
            type="number"
            id="weight"
            error={getErrorMessage("weight")}
            placeholder="Enter Weight"
            value={formData.weight}
            onChange={(val) => handleChange("weight", val)}
            disabled={loading}
          />

          {/* Dimensions */}
          <div className="w-full">
            <label className="text-subtitle2 text-secondary/90 pl-2 block mb-1">
              Dimensions (cm)
            </label>
            <div className="flex flex-row items-center gap-1.5 w-full">
              <div className="flex gap-1.5 w-full items-center">
                {["length", "breadth", "height"].map((dim) => (
                  <InputField
                    key={dim}
                    id={dim}
                    error={getErrorMessage(dim)}
                    value={formData[dim]}
                    onChange={(val) => handleChange(dim, val)}
                    placeholder={dim[0].toUpperCase()}
                    type="number"
                    className="w-full flex-grow"
                    disabled={loading}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Mode Selection */}
          <SelectMode
            label="Select Mode"
            name="serviceMode"
            id="serviceMode"
            error={getErrorMessage("serviceMode")}
            options={modeOptions}
            selectedValue={formData.serviceMode}
            onChange={(val) => handleChange("serviceMode", val)}
            selectedClassName="bg-primary/10"
            disabled={loading}
          />
          <SelectMode
            label="Payment Mode"
            name="paymentMode"
            id="paymentMode"
            error={getErrorMessage("paymentMode")}
            options={paymentOption}
            selectedValue={formData.paymentMode}
            onChange={(val) => handleChange("paymentMode", val)}
            selectedClassName="bg-primary/10"
            disabled={loading}
          />

          {/* Shipping Value */}
          <InputField
            label="Shipmen Value"
            type="number"
            id="shipmentValue"
            error={getErrorMessage("shipmentValue")}
            name="shipmentValue"
            placeholder="Enter Shipment Value"
            value={formData.shipmentValue}
            onChange={(val) => handleChange("shipmentValue", val)}
            disabled={loading}
          />

          {/* Buttons */}
          <div className="flex flex-row justify-start items-stretch md:items-end gap-4 mt-2">
            <Button
              type="submit"
              variant="default"
              className="w-[77%] "
              isLoading={loading}
            >
              Calculate Rate
            </Button>
            <Button
              type="reset"
              variant="default"
              className="w-1/5 !bg-secondary/60 hover:!bg-secondary/70"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </Button>
          </div>
        </form>

        {/* Shipping Rates List */}

        {data.length > 0 && (
          <div className="flex flex-col w-full md:w-fit container gap-2">
            <h2 className="text-headline4 pl-1 font-nunito font-bold text-secondaryL/90">
              Shipping Rates List
            </h2>
            <ServiceabilityTable
              columns={columns}
              data={data}
              paymentMode={codCol}
              isPercentage={false}
              isCurrency={true}
            />
          </div>
        )}
      </div>
    </SpaceWrapper>
  );
};

export default RateCalculator;
